// src/components/admin/payroll/components/BulkActionToolbar.js
// Bulk action toolbar for efficient employee validation and selection

import React, { useState, useEffect, useCallback } from 'react';
import {
    CheckSquare, Square, CheckCircle, XCircle, Flag,
    Search, Filter, ChevronDown, X, RefreshCw
} from 'lucide-react';
import { useTheme } from '../../../../contexts/ThemeContext';
import payrollService from '../../../../services/payroll.service';

const BulkActionToolbar = ({
    period,
    selectedIds,
    onSelectAll,
    onClearSelection,
    onBulkValidate,
    totalCount,
    filteredCount,
    departments = [],
    currentFilters = {},
    onFilterChange,
    onSearch,
    loading = false
}) => {
    const { theme } = useTheme();
    const [searchTerm, setSearchTerm] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [localDepartments, setLocalDepartments] = useState(departments);

    // Ensure currentFilters is never undefined/null to prevent Object.keys errors
    const safeFilters = currentFilters || {};

    // Fetch departments if not provided
    useEffect(() => {
        const fetchDepartments = async () => {
            if (departments.length === 0) {
                try {
                    const result = await payrollService.getDepartments();
                    if (result.success) {
                        setLocalDepartments(result.departments);
                    }
                } catch (err) {
                    console.error('Error fetching departments:', err);
                }
            }
        };
        fetchDepartments();
    }, [departments]);

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (onSearch) {
                onSearch(searchTerm);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm, onSearch]);

    const handleBulkAction = async (action) => {
        if (selectedIds.length === 0) return;

        setActionLoading(true);
        try {
            await onBulkValidate(selectedIds, action);
        } finally {
            setActionLoading(false);
        }
    };

    const hasSelection = selectedIds.length > 0;
    const allSelected = selectedIds.length === filteredCount && filteredCount > 0;

    return (
        <div className={`rounded-lg border ${
            theme === 'dark'
                ? 'bg-gray-800 border-gray-700'
                : 'bg-white border-gray-200'
        }`}>
            {/* Main Toolbar */}
            <div className="p-3 flex items-center gap-3 flex-wrap">
                {/* Selection Controls */}
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => allSelected ? onClearSelection() : onSelectAll()}
                        className={`p-2 rounded-lg transition-colors ${
                            theme === 'dark'
                                ? 'hover:bg-gray-700 text-gray-300'
                                : 'hover:bg-gray-100 text-gray-600'
                        }`}
                        title={allSelected ? 'Clear selection' : 'Select all'}
                    >
                        {allSelected ? (
                            <CheckSquare className="w-5 h-5 text-blue-500" />
                        ) : hasSelection ? (
                            <div className="relative">
                                <Square className="w-5 h-5" />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-2 h-0.5 bg-current" />
                                </div>
                            </div>
                        ) : (
                            <Square className="w-5 h-5" />
                        )}
                    </button>

                    {hasSelection && (
                        <span className={`text-sm font-medium px-2 py-1 rounded ${
                            theme === 'dark'
                                ? 'bg-blue-900/30 text-blue-400'
                                : 'bg-blue-50 text-blue-600'
                        }`}>
                            {selectedIds.length} selected
                        </span>
                    )}
                </div>

                {/* Bulk Actions (visible when selection exists) */}
                {hasSelection && (
                    <>
                        <div className={`w-px h-6 ${
                            theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                        }`} />

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handleBulkAction('approve')}
                                disabled={actionLoading}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 ${
                                    actionLoading
                                        ? 'bg-gray-300 text-gray-500 cursor-wait'
                                        : 'bg-green-600 text-white hover:bg-green-700'
                                }`}
                            >
                                <CheckCircle className="w-4 h-4" />
                                Validate
                            </button>

                            <button
                                onClick={() => handleBulkAction('flag')}
                                disabled={actionLoading}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 ${
                                    actionLoading
                                        ? 'bg-gray-300 text-gray-500 cursor-wait'
                                        : theme === 'dark'
                                            ? 'bg-yellow-900/50 text-yellow-400 hover:bg-yellow-900/70'
                                            : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                }`}
                            >
                                <Flag className="w-4 h-4" />
                                Flag
                            </button>

                            <button
                                onClick={() => handleBulkAction('skip')}
                                disabled={actionLoading}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 ${
                                    actionLoading
                                        ? 'bg-gray-300 text-gray-500 cursor-wait'
                                        : theme === 'dark'
                                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                <XCircle className="w-4 h-4" />
                                Skip
                            </button>

                            <button
                                onClick={onClearSelection}
                                className={`p-1.5 rounded-lg ${
                                    theme === 'dark'
                                        ? 'hover:bg-gray-700 text-gray-400'
                                        : 'hover:bg-gray-100 text-gray-500'
                                }`}
                                title="Clear selection"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </>
                )}

                {/* Spacer */}
                <div className="flex-1" />

                {/* Search */}
                <div className="relative">
                    <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                        theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                    }`} />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search employees..."
                        className={`pl-9 pr-4 py-2 rounded-lg text-sm w-64 ${
                            theme === 'dark'
                                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500'
                                : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                        } border focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            className={`absolute right-3 top-1/2 -translate-y-1/2 ${
                                theme === 'dark' ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-500'
                            }`}
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>

                {/* Filter Toggle */}
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${
                        showFilters || Object.keys(safeFilters).some(k => safeFilters[k])
                            ? theme === 'dark'
                                ? 'bg-blue-900/30 text-blue-400 border border-blue-800'
                                : 'bg-blue-50 text-blue-600 border border-blue-200'
                            : theme === 'dark'
                                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    } border`}
                >
                    <Filter className="w-4 h-4" />
                    Filters
                    {Object.keys(safeFilters).some(k => safeFilters[k]) && (
                        <span className="w-2 h-2 bg-blue-500 rounded-full" />
                    )}
                </button>
            </div>

            {/* Filter Panel (expandable) */}
            {showFilters && (
                <div className={`px-3 pb-3 pt-0 border-t ${
                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>
                    <div className="flex items-center gap-4 mt-3 flex-wrap">
                        {/* Department Filter */}
                        <div className="flex items-center gap-2">
                            <label className={`text-sm ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                Department:
                            </label>
                            <select
                                value={safeFilters.department || ''}
                                onChange={(e) => onFilterChange?.({ ...safeFilters, department: e.target.value || null })}
                                className={`px-3 py-1.5 rounded-lg text-sm ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600 text-white'
                                        : 'bg-white border-gray-200 text-gray-900'
                                } border`}
                            >
                                <option value="">All Departments</option>
                                {localDepartments.map(dept => (
                                    <option key={dept} value={dept}>{dept}</option>
                                ))}
                            </select>
                        </div>

                        {/* Status Filter */}
                        <div className="flex items-center gap-2">
                            <label className={`text-sm ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                Status:
                            </label>
                            <select
                                value={safeFilters.status || 'all'}
                                onChange={(e) => onFilterChange?.({ ...safeFilters, status: e.target.value })}
                                className={`px-3 py-1.5 rounded-lg text-sm ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600 text-white'
                                        : 'bg-white border-gray-200 text-gray-900'
                                } border`}
                            >
                                <option value="all">All Status</option>
                                <option value="auto_validated">Auto-Validated</option>
                                <option value="needs_review">Needs Review</option>
                                <option value="has_issues">Has Issues</option>
                            </select>
                        </div>

                        {/* Clear Filters */}
                        {Object.keys(safeFilters).some(k => safeFilters[k] && safeFilters[k] !== 'all') && (
                            <button
                                onClick={() => onFilterChange?.({ department: null, status: 'all' })}
                                className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 ${
                                    theme === 'dark'
                                        ? 'text-gray-400 hover:text-gray-300'
                                        : 'text-gray-600 hover:text-gray-700'
                                }`}
                            >
                                <X className="w-3 h-3" />
                                Clear filters
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Results Summary */}
            <div className={`px-3 py-2 text-sm flex items-center justify-between border-t ${
                theme === 'dark'
                    ? 'bg-gray-900/50 border-gray-700 text-gray-400'
                    : 'bg-gray-50 border-gray-200 text-gray-600'
            }`}>
                <span>
                    Showing <strong>{filteredCount}</strong> of <strong>{totalCount}</strong> employees
                </span>
                {loading && (
                    <span className="flex items-center gap-2">
                        <RefreshCw className="w-3 h-3 animate-spin" />
                        Loading...
                    </span>
                )}
            </div>
        </div>
    );
};

export default BulkActionToolbar;
